import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '#/shared/ui/input'
import { Select } from '#/shared/ui/select'
import type { SelectOption } from '#/shared/ui/select'
import {
  SEGMENTED_TAB_VARIANTS,
  SegmentedTabs,
} from '#/shared/ui/segmented-tabs'

import { CAMPAIGN_TASK_TYPE_ORDER } from '../model/use-campaign-composer'
import type { UseCampaignComposerResult } from '../model/use-campaign-composer'
import { CampaignComposerControls } from './campaign-composer-controls'

interface CampaignTaskComposerProps {
  composer: UseCampaignComposerResult
}

/** '' is a real choice, not an empty one — it lands the task on whoever owns
 *  the quote, which is the right default for work about that quote. */
const OWN_AGENT = ''

/**
 * A task step: agent work the sequence queues instead of a message.
 *
 * It has no body, no template and no line — nothing is sent. What it needs is
 * a title (stored in `subject`, where an email step keeps its subject line),
 * the kind of work, who it lands on, and when it is due. The send rate and the
 * hold still apply, so the shared controls stay.
 */
export function CampaignTaskComposer({ composer }: CampaignTaskComposerProps) {
  const { t } = useTranslation()
  const assigneeOptions: SelectOption[] = useMemo(
    () => [
      { value: OWN_AGENT, label: t('campaigns.composer.task.ownAgent') },
      ...composer.assignableUsers.map((user) => ({
        value: user.id,
        label: user.name,
      })),
    ],
    [composer.assignableUsers, t],
  )

  return (
    <div className="flex flex-col gap-4 p-3.5">
      <label className="flex flex-col gap-1.5">
        <span className="text-2xs font-medium text-warm-500">
          {t('campaigns.composer.task.title')}
        </span>
        <Input
          value={composer.subject}
          onChange={(event) => composer.setSubject(event.target.value)}
          placeholder={t('campaigns.composer.task.titlePlaceholder')}
        />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-gray-500">
          {t('campaigns.composer.task.kind')}
        </span>
        <SegmentedTabs
          items={CAMPAIGN_TASK_TYPE_ORDER.map((type) => ({
            value: type,
            render: () => t(`campaigns.composer.task.type.${type}`),
          }))}
          value={composer.taskType}
          onValueChange={composer.setTaskType}
          variant={SEGMENTED_TAB_VARIANTS.PILL}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-gray-500">
          {t('campaigns.composer.task.assignee')}
        </span>
        <Select
          selectSize="sm"
          options={assigneeOptions}
          value={composer.assigneeUserId}
          onValueChange={composer.setAssigneeUserId}
          disabled={composer.assigneesLoading}
          className="w-56 rounded-full bg-gray-100"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-gray-500">
          {t('campaigns.composer.task.due')}
        </span>
        <Input
          type="number"
          min={0}
          max={365}
          inputMode="numeric"
          value={composer.dueOffsetDays}
          onChange={(event) => composer.setDueOffsetDays(event.target.value)}
          placeholder={t('campaigns.composer.task.duePlaceholder')}
          className="w-24"
        />
        <span className="text-xs text-gray-300">
          {t('campaigns.composer.task.dueHint')}
        </span>
      </div>

      <CampaignComposerControls composer={composer} />
    </div>
  )
}
